import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Chip,
  Stack,
  Link,
  IconButton,
  Divider,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { AppDispatch, RootState } from '../store/store';
import { selectCurrentUser } from '../store/authSlice';
import { fetchClaimById, getClaimAttachment, deleteClaimAttachment, uploadClaimAttachment } from '../services/claimsApi';
import { claimColors, claimSx } from '../styles/claimPageStyles';

const ClaimEditAttachments: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { currentClaim: claim, loading, error } = useSelector((state: RootState) => state.claims);
  const user = useSelector(selectCurrentUser);

  useEffect(() => {
    if (id) {
      dispatch(fetchClaimById(id));
    }
  }, [dispatch, id]);

  const handleAttachmentDownload = async (claimId: string, attachmentId: string) => {
    try {
      const response = await getClaimAttachment(claimId, attachmentId);
      const attachment = claim?.attachments?.find(a => a.id === attachmentId);
      const filename = attachment?.original_filename || 'attachment';

      const link = document.createElement('a');
      link.style.display = 'none';
      const url = window.URL.createObjectURL(response.data);
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (err) {
      console.error('Error downloading attachment:', err);
    }
  };

  const handleAttachmentDelete = async (claimId: string, attachmentId: string) => {
    try {
      await deleteClaimAttachment(claimId, attachmentId);
      dispatch(fetchClaimById(claimId));
    } catch (err) {
      console.error('Error deleting attachment:', err);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const uploadFile = async (file: File) => {
    if (!claim) return;

    let type: 'document' | 'image' | 'video' = 'document';
    const fileType = file.type.toLowerCase();
    if (fileType.includes('image/jpeg') || fileType.includes('image/png')) {
      type = 'image';
    } else if (fileType.includes('video/mp4')) {
      type = 'video';
    } else if (fileType.includes('application/pdf') || fileType.includes('text/plain')) {
      type = 'document';
    }

    try {
      await uploadClaimAttachment(claim.id, file, type, user?.id);
      dispatch(fetchClaimById(claim.id));
    } catch (err) {
      console.error('Error uploading file:', err);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
    event.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  if (loading) {
    return (
      <Container>
        <Typography color="text.secondary">Loading...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!claim) {
    return (
      <Container>
        <Typography color="text.secondary">Claim not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => navigate(`/claims/${id}`)}
            sx={claimSx.btnOutlined}
          >
            Back
          </Button>
        </Box>

        <Paper elevation={0} sx={claimSx.mainPaper}>
          <Stack spacing={4}>
            <Box sx={claimSx.headerBar}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="600" sx={{ color: 'text.primary' }}>
                  Claim: <Box component="span" sx={claimSx.claimNumber}>{claim.claim_number}</Box>
                </Typography>
                <Typography variant="body1" fontWeight="500" sx={{ color: 'text.secondary' }}>
                  <Box component="span" sx={{ fontWeight: 'bold' }}>Facility:</Box> {claim.facility?.location_code || 'Loading...'}
                </Typography>
                <Typography variant="body1" fontWeight="500" sx={{ color: 'text.secondary' }}>
                  <Box component="span" sx={{ fontWeight: 'bold' }}>Unit Number:</Box> {claim.facility_unit}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1, borderColor: 'divider' }} />

            <Paper elevation={0} sx={{ ...claimSx.sectionCard, p: 3 }}>
              <Box sx={claimSx.sectionTitleRow}>
                <Typography variant="h6" sx={{ color: claimColors.navy, fontWeight: 600 }}>
                  Claim Attachments
                </Typography>
              </Box>
              <Stack spacing={2}>
                {claim.attachments && claim.attachments.length > 0 ? (
                  claim.attachments.map((attachment, index) => (
                    <Box
                      key={attachment.id}
                      sx={claimSx.nestedRow}
                    >
                      <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.primary', minWidth: '120px' }}>
                        Attachment {index + 1}:
                      </Typography>
                      <Chip
                        label={attachment.type}
                        size="small"
                        sx={claimSx.attachmentChip}
                      />
                      <Link
                        component="button"
                        onClick={() => handleAttachmentDownload(claim.id, attachment.id)}
                        sx={claimSx.link}
                      >
                        {attachment.original_filename}
                      </Link>
                      <Box sx={{ flexGrow: 1 }} />
                      {!attachment.user_id ? (
                        <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                          (uploaded by tenant)
                        </Typography>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => handleAttachmentDelete(claim.id, attachment.id)}
                          sx={{
                            color: '#ef5350',
                            '&:hover': {
                              backgroundColor: 'rgba(239, 83, 80, 0.1)',
                              color: '#f44336',
                            },
                          }}
                          aria-label="Delete attachment"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))
                ) : null}
              </Stack>
              <Box
                sx={{
                  mt: 3,
                  p: 4,
                  border: '2px dashed',
                  borderColor: isDragging ? claimColors.red : 'divider',
                  borderRadius: 2,
                  backgroundColor: isDragging ? 'rgba(220, 53, 69, 0.04)' : claimColors.cardMuted,
                  transition: 'border-color 0.2s, background-color 0.2s',
                  textAlign: 'center',
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  accept=".pdf,.txt,.jpg,.jpeg,.png,.mp4"
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileUpload}
                />
                <label htmlFor="file-upload" style={{ cursor: 'pointer' }}>
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                    {claim.attachments && claim.attachments.length > 0
                      ? 'Drop files here or click to add more'
                      : 'Drop files here or click to upload'}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    component="span"
                    sx={claimSx.btnOutlinedAccent}
                  >
                    Upload Documents
                  </Button>
                </label>
              </Box>
            </Paper>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
};

export default ClaimEditAttachments;
